import { DEFAULT_WIDGET_SETTINGS, type PreviewReview } from '@flexbyt/shared';
import { ReviewStars, ReviewerAvatar, SourceGlyph, cx } from '@flexbyt/widget-preview';
import { useMemo, useState } from 'react';
import { ALL_DEMO_REVIEWS } from '../../content/demo-reviews.js';
import { WidgetShowcase } from '../WidgetShowcase.js';
import { AppPanel } from './Panel.js';

/**
 * The review library, working — and the widget it feeds.
 *
 * ===========================================================================
 * The one section on this page that shows a lot of reviews
 * ===========================================================================
 * And it can, because it is the only place where reviews are the *subject of a
 * control* rather than content being displayed. This has to be unmistakably
 * application UI — checkboxes, a selection toolbar, publish states — so that
 * nobody confuses it with the finished website further down. `AppPanel` gives
 * it the ReviewsJet mark and a screen name in its title bar for exactly that
 * reason; see the note there for why two kinds of window is the whole point of
 * the middle of this page.
 *
 * ===========================================================================
 * Publishing changes what is on the website, and now you can watch it
 * ===========================================================================
 * The section used to be the library alone, with a paragraph above it saying
 * "you choose which reviews appear on your website". A visitor published a
 * review, watched a badge change from Pending to Live — and had to take the
 * important half on trust, because the website was not in the picture. "Live"
 * is a word; the widget is the thing it means.
 *
 * So `preview` puts the real grid widget underneath the list, drawn from the
 * published rows and nothing else. Unpublish a review and it leaves the widget
 * while you are looking at it. That is the entire manage story in one gesture,
 * and it replaces about forty words of copy that were making the same claim
 * less convincingly.
 *
 * It is the *real* renderer — `@flexbyt/widget-preview`, the same code that
 * runs on customers' sites and the same code the dashboard's editor draws with.
 * A mocked-up card here would be a promise the product then has to keep.
 *
 * ===========================================================================
 * Real interactions, not a screenshot of interactions
 * ===========================================================================
 * Bulk publish and unpublish exist in the product, as `POST /api/reviews/bulk`.
 * Delete is on that endpoint too, and the bar still shows it, but it is
 * disabled here: emptying the library that the widget below is fed from is a
 * dead end for the demonstration this section exists to make.
 */

type Row = {
  /** The review itself, so the widget below can be handed the real thing. */
  review: PreviewReview;
  tags: string[];
  published: boolean;
};

/**
 * Six rows drawn from the demo set, with tags added.
 *
 * Tags are a real feature — the product has a `Tag` model and widgets can be
 * filtered by tag — but the demo reviews are written per source rather than per
 * theme, so the labels are attached here rather than pretending the copy came
 * with them.
 *
 * A mix of published and pending on purpose — five live, one waiting. A list
 * where everything is already live has no moderation queue in it, which is the
 * one thing this section is about.
 */
const TAGS = [
  ['Service'],
  ['Product', 'Delivery'],
  ['Support'],
  ['Value'],
  ['Service', 'Team'],
  ['Product'],
];

const INITIAL_ROWS: Row[] = ALL_DEMO_REVIEWS.filter((_, index) => index % 3 === 0)
  .slice(0, 6)
  .map((review: PreviewReview, index: number) => ({
    review,
    tags: TAGS[index] ?? ['Service'],
    published: index < 5,
  }));

export function ManageLibrary({
  preview = false,
  className,
}: {
  /**
   * Draw the widget the published rows feed, underneath the library.
   *
   * Off by default because not every surface has room for it — a comparison
   * page wants the list alone. Where there is room it is worth a great deal:
   * it is the difference between reading that publishing changes your site and
   * seeing it do so.
   */
  preview?: boolean;
  className?: string;
}) {
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;

  const published = useMemo(
    () => rows.filter((row) => row.published).map((row) => row.review),
    [rows]
  );

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(
      allSelected ? new Set() : new Set(rows.map((row) => row.review.id))
    );
  }

  function setPublished(id: string, published: boolean) {
    setRows((current) =>
      current.map((row) =>
        row.review.id === id ? { ...row, published } : row
      )
    );
  }

  function bulk(action: 'publish' | 'unpublish') {
    setRows((current) =>
      current.map((row) =>
        selected.has(row.review.id)
          ? { ...row, published: action === 'publish' }
          : row
      )
    );
    setSelected(new Set());
  }

  return (
    <div className={className}>
      <AppPanel
        screen="Review library"
        meta={`${rows.length} ${rows.length === 1 ? 'review' : 'reviews'} · 5 sources`}
      >
        {/*
          Select-all stays put whether or not anything is chosen, so ticking a
          row does not shove the table. The bulk actions are not in this row:
          they float over the bottom of the list, which is where the cursor
          already is after selecting.
        */}
        <div className="flex min-h-14 flex-wrap items-center gap-3 border-b border-ink-100 px-5 py-2.5">
          <label className="flex cursor-pointer items-center gap-2.5 text-body-sm text-ink-700">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(node) => {
                // Indeterminate is a property, not an attribute — it cannot be
                // set from JSX, and without it "some selected" looks like "none".
                if (node) node.indeterminate = someSelected;
              }}
              onChange={toggleAll}
              className="size-4 rounded border-ink-300 accent-accent-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600"
            />
            {selected.size > 0 ? (
              <span className="nums font-medium text-ink-950">
                {selected.size} selected
              </span>
            ) : (
              <span>Select all</span>
            )}
          </label>

          <p className="ml-auto text-caption text-ink-500">
            {published.length} of {rows.length} published
          </p>
        </div>

        <div className="relative">
          <ul className="divide-y divide-ink-100">
            {rows.map((row) => {
              const isSelected = selected.has(row.review.id);
              return (
                <li
                  key={row.review.id}
                  className={cx(
                    'flex items-start gap-3 px-5 py-4 sm:gap-4',
                    'transition-colors duration-[var(--duration-fast)]',
                    /*
                      An inset rule down the left edge of a chosen row, drawn as
                      a shadow rather than a border so it costs no layout — a
                      real 2px border would shift every cell in the row sideways
                      the moment it was ticked.
                    */
                    isSelected
                      ? 'bg-accent-50 shadow-[inset_3px_0_0_0_var(--color-accent-600)]'
                      : 'hover:bg-ink-25'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(row.review.id)}
                    aria-label={`Select the review from ${row.review.authorName}`}
                    className="mt-1 size-4 shrink-0 rounded border-ink-300 accent-accent-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600"
                  />

                  {/*
                    The renderer's own avatar, not an `<img>` written here.

                    It carries the fallback a review card gets when somebody has
                    no picture — initials on a tinted disc rather than a broken
                    image — which is a real state in a real library and one a
                    hand-rolled tag on a marketing page always forgets.
                  */}
                  <div className="mt-0.5 shrink-0">
                    <ReviewerAvatar
                      name={row.review.authorName}
                      src={row.review.authorAvatar}
                      size={36}
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                      <p className="text-body-sm font-medium text-ink-950">
                        {row.review.authorName}
                      </p>
                      <SourceGlyph
                        source={row.review.source}
                        className="size-3.5 shrink-0"
                      />
                      {/*
                        The renderer's stars, in the product's own default star
                        colour, so the gold in the library is the same gold as
                        the gold in the widget below it. Two star components on
                        one screen is exactly the drift the shared package
                        exists to prevent.
                      */}
                      <ReviewStars
                        rating={row.review.rating}
                        color={DEFAULT_WIDGET_SETTINGS.starColor}
                        size={12}
                      />
                    </div>

                    <p className="mt-1.5 line-clamp-2 text-body-sm text-ink-600">
                      {row.review.content}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {/*
                        Live/Pending used to sit on the right of the row, which
                        is where a control belongs. Publish and unpublish are
                        the thing you do with a review, so they sit there now;
                        the status lives with the tags, as a fact about the
                        row rather than as the thing to click.

                        A word plus a dot, never a colour alone — the
                        distinction has to survive somebody who cannot
                        separate the two hues.
                      */}
                      <span
                        className={cx(
                          'inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-micro font-medium ring-1',
                          row.published
                            ? 'bg-positive-50 text-positive-800 ring-positive-200'
                            : 'bg-ink-50 text-ink-600 ring-ink-150'
                        )}
                      >
                        <span
                          aria-hidden
                          className={cx(
                            'size-1.5 rounded-full',
                            row.published ? 'bg-positive-700' : 'bg-ink-300'
                          )}
                        />
                        {row.published ? 'Live' : 'Pending'}
                      </span>
                      {row.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded bg-ink-50 px-1.5 py-0.5 text-micro text-ink-600 ring-1 ring-ink-150"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <RowButton
                    published={row.published}
                    author={row.review.authorName}
                    onClick={() => setPublished(row.review.id, !row.published)}
                  />
                </li>
              );
            })}
          </ul>

          {selected.size > 0 ? (
            /*
              Floated over the list rather than inserted into it. A bar that
              takes up a row at the bottom would hide the last review the
              moment you selected it; an overlay sits on top of the reviews
              that are already there, which is where the cursor is.
            */
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 pt-16 pb-4">
              <div
                role="toolbar"
                aria-label="Bulk actions"
                className="rj-rise pointer-events-auto on-ink flex flex-wrap items-center gap-2 rounded-xl bg-ink-950 px-2.5 py-2 text-white shadow-lg ring-1 ring-white/10"
              >
                <BulkButton onClick={() => bulk('publish')}>Publish</BulkButton>
                <BulkButton onClick={() => bulk('unpublish')}>Unpublish</BulkButton>
                <BulkButton disabled tone="critical">
                  Delete
                </BulkButton>
              </div>
            </div>
          ) : null}
        </div>
      </AppPanel>

      {preview ? <PublishedWidget reviews={published} /> : null}
    </div>
  );
}

/**
 * What the published rows look like on a website, updating as they change.
 *
 * ===========================================================================
 * Deliberately not another website mock-up
 * ===========================================================================
 * The Display section further down the homepage draws a whole site — browser
 * chrome, header, navigation — because its subject is *where the reviews go*.
 * This one's subject is narrower: which reviews are in it. Repeating the site
 * frame here would make the two sections look like the same demonstration
 * twice, which is the exact confusion the panels were introduced to fix.
 *
 * So it is the widget on a plain canvas with a connecting line above it, and
 * the sentence that names the relationship: this is the list, on your site.
 */
function PublishedWidget({ reviews }: { reviews: PreviewReview[] }) {
  return (
    <div className="mt-0">
      {/*
        The seam between the two. A short vertical rule and an arrowhead, so the
        widget reads as *downstream of* the list rather than as the next item in
        a stack of panels. It is drawn rather than described because the
        relationship is the point and a caption alone would not carry it.
      */}
      <div aria-hidden className="flex flex-col items-center">
        <span className="h-8 w-px bg-ink-200" />
        <svg viewBox="0 0 12 8" className="-mt-px h-2 w-3 text-ink-200" aria-hidden>
          <path d="M0 0h12L6 8z" fill="currentColor" />
        </svg>
      </div>

      <div className="rj-canvas mt-2 rounded-2xl p-4 ring-1 ring-ink-150 sm:p-8">
        <p className="mb-5 flex flex-wrap items-baseline gap-x-2 text-caption text-ink-500">
          <span className="font-medium text-ink-950">On your website</span>
          <span aria-live="polite">
            {reviews.length === 0
              ? 'nothing published, so the widget is empty'
              : `${reviews.length} published ${reviews.length === 1 ? 'review' : 'reviews'}, and only those`}
          </span>
        </p>

        {reviews.length === 0 ? (
          /*
            An honest empty widget rather than a hidden one. Unpublishing
            everything is the fastest way to prove the claim this section makes,
            and a component that vanished at that moment would look like a bug
            in the demo rather than the answer to the question.
          */
          <div className="grid place-items-center rounded-xl border border-dashed border-ink-200 bg-white/60 px-6 py-14 text-center">
            <p className="text-body-sm text-ink-600">
              Your widget is empty. Publish a review above and it appears here.
            </p>
          </div>
        ) : (
          <WidgetShowcase
            /*
              Remounted whenever the set changes. The grid is plain CSS and would
              survive reconciliation, but every other layout in this renderer
              measures the DOM on mount and writes inline positions — keying on
              the published set costs nothing and means switching this to masonry
              later is a one-word change rather than a bug.
            */
            key={reviews.map((review) => review.id).join(',')}
            type="grid"
            reviews={reviews}
          />
        )}
      </div>
    </div>
  );
}

function RowButton({
  published,
  author,
  onClick,
}: {
  published: boolean;
  author: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        published ? `Unpublish ${author}'s review` : `Publish ${author}'s review`
      }
      className={cx(
        'mt-0.5 shrink-0 rounded-md px-2.5 py-1.5 text-body-sm font-medium ring-1',
        'whitespace-nowrap transition-colors duration-[var(--duration-fast)]',
        published
          ? 'text-ink-700 ring-ink-200 hover:bg-ink-50 hover:text-ink-950'
          : 'text-accent-700 ring-accent-200 hover:bg-accent-50'
      )}
    >
      {published ? 'Unpublish' : 'Publish'}
    </button>
  );
}

function BulkButton({
  children,
  onClick,
  tone = 'neutral',
  disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  tone?: 'neutral' | 'critical';
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'rounded-md px-2.5 py-1.5 text-body-sm font-medium ring-1',
        'transition-colors duration-[var(--duration-fast)]',
        'disabled:pointer-events-none disabled:opacity-40',
        tone === 'critical'
          ? 'text-critical-300 ring-critical-400/40 hover:bg-white/5'
          : 'text-white/90 ring-white/25 hover:bg-white/10 hover:text-white hover:ring-white/40'
      )}
    >
      {children}
    </button>
  );
}
